import { Dialog } from '@platform/design-system';
import { Plus } from 'lucide-react';
import { usePageContext } from '../../usePageContext.ts';
import { isWidgetVisible, WIDGET_DEFINITIONS } from './registry.ts';
import { WIDGET_PRESETS, widgetFromPreset, type PresetGroup, type WidgetPreset } from './widget-presets.ts';
import styles from './Widgets.module.css';

const PRESET_GROUPS = Object.keys(WIDGET_PRESETS) as readonly PresetGroup[];

const ICON_SIZE = 20;

export type WidgetGalleryProps = {
  open: boolean;
  full: boolean;
  financeVisible: boolean;
  onClose: () => void;
  onAdd: (preset: WidgetPreset) => void;
};

const presetTitleKey = (preset: WidgetPreset): string =>
  preset.kind === 'kpi' && 'tile' in preset.settings
    ? `dashboard.overview.${preset.settings.tile}`
    : `dashboard.widgets.preset.${preset.key}.title`;

const presetDescriptionKey = (preset: WidgetPreset): string =>
  preset.kind === 'kpi'
    ? 'dashboard.widgets.preset.kpi.description'
    : `dashboard.widgets.preset.${preset.key}.description`;

export const WidgetGallery = ({ open, full, financeVisible, onClose, onAdd }: WidgetGalleryProps) => {
  const { t } = usePageContext();
  return (
    <Dialog
      open={open}
      size="wide"
      title={t('dashboard.widgets.galleryTitle')}
      subtitle={full ? t('dashboard.widgets.galleryFull') : t('dashboard.widgets.gallerySubtitle')}
      closeLabel={t('common.close')}
      onClose={onClose}
    >
      {PRESET_GROUPS.map((group) => (
        <section
          key={group}
          className={styles['galleryGroup']}
          aria-label={t(`dashboard.widgets.group.${group}`)}
        >
          <h3>{t(`dashboard.widgets.group.${group}`)}</h3>
          <ul className={styles['presets']}>
            {WIDGET_PRESETS[group]
              .filter((preset) =>
                isWidgetVisible(widgetFromPreset(preset, preset.key), {
                  financeVisible,
                  productFiltered: false,
                }),
              )
              .map((preset) => {
                const Icon = WIDGET_DEFINITIONS[preset.kind].icon;
                return (
                  <li key={preset.key}>
                    <button
                      type="button"
                      className={styles['preset']}
                      disabled={full}
                      onClick={() => {
                        onAdd(preset);
                      }}
                    >
                      <Icon size={ICON_SIZE} aria-hidden="true" />
                      <span className={styles['presetText']}>
                        <strong>{t(presetTitleKey(preset))}</strong>
                        <span>{t(presetDescriptionKey(preset))}</span>
                      </span>
                      <Plus size={ICON_SIZE} aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
          </ul>
        </section>
      ))}
    </Dialog>
  );
};
